import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { KeyRound, Loader2, LogIn, MailCheck, UserPlus } from 'lucide-react';
import { formatarTelefone, telefoneValido } from '@miolos/core';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { MarcaMiolos } from '@/components/Logo';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/lib/api';

function MolduraAuth({
  titulo,
  subtitulo,
  children,
  rodape,
}: {
  titulo: string;
  subtitulo: string;
  children: React.ReactNode;
  rodape: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-md py-8">
      <div className="flex flex-col items-center gap-2 pb-5 text-center">
        <MarcaMiolos tamanho={56} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          A plataforma ágil para organizar, gerar e controlar miolos de papelaria personalizada
        </span>
      </div>
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <h1 className="text-xl font-semibold tracking-tight">{titulo}</h1>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{subtitulo}</p>
        <div className="mt-6">{children}</div>
      </div>
      <p className="mt-4 text-center text-sm text-muted-foreground">{rodape}</p>
    </div>
  );
}

export function Entrar() {
  const { entrar, usuario } = useAuth();
  const navegar = useNavigate();
  const local = useLocation();
  const [params] = useSearchParams();
  // Quem chega por um convite de teste volta para ele depois do login, e o convite é aplicado.
  const convite = params.get('convite');
  /**
   * Para onde voltar depois de entrar.
   *
   * Quem clica em "Entrar para consultar" na biblioteca volta para o trabalho que
   * estava lendo, e não para a lista de miolos.
   */
  const voltar = params.get('voltar');
  const destino =
    (local.state as { de?: string } | null)?.de ?? (convite ? `/convidar/${convite}` : voltar || '/meus-miolos');

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (usuario) return <Navigate to={destino} replace />;

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await entrar(email, senha);
      navegar(destino, { replace: true });
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <MolduraAuth
      titulo="Entrar na sua área"
      subtitulo="Acesse seus miolos salvos, continue editando e gere novos PDFs."
      rodape={
        <>
          Ainda não tem conta?{' '}
          <Link className="font-medium text-primary hover:underline" to="/criar-conta">
            Criar agora
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submeter}>
        <Campo rotulo="E-mail">
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@email.com"
          />
        </Campo>
        <Campo rotulo="Senha">
          <Input
            type="password"
            required
            autoComplete="current-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            placeholder="••••••••"
          />
        </Campo>

        <div className="text-right">
          <Link className="text-xs font-medium text-primary hover:underline" to="/esqueci-senha">
            Esqueci minha senha
          </Link>
        </div>

        {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

        <Button type="submit" className="w-full" disabled={enviando}>
          {enviando ? <Loader2 className="animate-spin" /> : <LogIn />}
          Entrar
        </Button>

        {/*
          A demonstração é **pedida**, não entregue.

          A conta de demonstração continua existindo (é o que a equipe e os
          testes usam), mas as credenciais não ficam escritas na tela de entrar
          para qualquer visitante copiar: quem quer conhecer o gerador pede o
          teste na página de demonstração, e a liberação vai por e-mail.
        */}
        <p className="rounded-md bg-muted/60 p-2.5 text-center text-xs leading-relaxed text-muted-foreground">
          Quer conhecer o gerador antes de criar conta?{' '}
          <Link to="/demo" className="underline">
            Peça uma demonstração
          </Link>{' '}
          — a liberação chega no seu e-mail.
        </p>
      </form>
    </MolduraAuth>
  );
}

export function CriarConta() {
  const { registrar, usuario } = useAuth();
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const convite = params.get('convite');

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Quem chegou por convite e já tem conta vai direto aplicar o convite.
  if (usuario) return <Navigate to={convite ? `/convidar/${convite}` : '/meus-miolos'} replace />;

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);

    if (senha.length < 10) {
      setErro('A senha precisa de 10 caracteres ou mais.');
      return;
    }
    if (senha !== confirmacao) {
      setErro('As senhas não coincidem.');
      return;
    }
    /**
     * O telefone é obrigatório: é por ele que o suporte fala com o cliente e
     * que o grupo de avisos é montado. Guardamos só os dígitos.
     */
    if (!telefone.trim()) {
      setErro('Informe um telefone com DDD para contato.');
      return;
    }
    if (!telefoneValido(telefone)) {
      setErro('Telefone inválido. Use o formato (11) 91234-5678.');
      return;
    }

    setEnviando(true);
    try {
      await registrar({ nome, email, senha, telefone });
      navegar(convite ? `/convidar/${convite}` : '/criar', { replace: true });
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível criar a conta.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <MolduraAuth
      titulo="Criar sua conta"
      subtitulo="Leva menos de um minuto. Depois é só montar o miolo e salvar na sua área."
      rodape={
        <>
          Já tem conta?{' '}
          <Link className="font-medium text-primary hover:underline" to="/entrar">
            Entrar
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submeter}>
        <Campo rotulo="Seu nome">
          <Input required value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Como devo te chamar" />
        </Campo>
        <Campo rotulo="E-mail">
          <Input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@email.com"
          />
        </Campo>
        <Campo
          rotulo="Telefone / WhatsApp"
          dica="Com DDD. É por aqui que avisamos sobre a sua agenda e o acesso."
        >
          <Input
            type="tel"
            required
            inputMode="tel"
            autoComplete="tel"
            maxLength={15}
            value={telefone}
            onChange={(e) => setTelefone(formatarTelefone(e.target.value))}
            placeholder="(11) 91234-5678"
          />
        </Campo>
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Senha" dica="Mínimo de 10 caracteres, com letra e número.">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
            />
          </Campo>
          <Campo rotulo="Confirmar senha">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              placeholder="••••••••"
            />
          </Campo>
        </div>

        {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

        <Button type="submit" className="w-full" disabled={enviando}>
          {enviando ? <Loader2 className="animate-spin" /> : <UserPlus />}
          Criar conta e continuar
        </Button>
      </form>
    </MolduraAuth>
  );
}

/* ------------------------------------------------------------------ */
/*  Recuperação de senha                                               */
/* ------------------------------------------------------------------ */

/**
 * Pede o link de redefinição. A resposta é sempre a mesma, exista ou não a
 * conta — é assim que a tela não vira um "descobridor de e-mails".
 */
export function EsqueciSenha() {
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await api.esqueciSenha(email.trim());
      setEnviado(true);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar o link.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <MolduraAuth
      titulo="Esqueci minha senha"
      subtitulo="Informe o e-mail da sua conta e enviamos um link para criar uma nova senha."
      rodape={
        <>
          Lembrou?{' '}
          <Link className="font-medium text-primary hover:underline" to="/entrar">
            Voltar para entrar
          </Link>
        </>
      }
    >
      {enviado ? (
        <div className="space-y-4 text-sm leading-relaxed">
          <div className="flex items-start gap-3 rounded-md bg-muted/60 p-3">
            <MailCheck className="mt-0.5 size-5 shrink-0 text-primary" />
            <p>
              Se <strong>{email}</strong> estiver cadastrado, o link de redefinição já saiu. Ele vale por
              uma hora. Confira também a caixa de spam.
            </p>
          </div>
          <Button asChild className="w-full">
            <Link to="/entrar">Voltar para entrar</Link>
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={submeter}>
          <Campo rotulo="E-mail">
            <Input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@email.com"
            />
          </Campo>

          {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

          <Button type="submit" className="w-full" disabled={enviando}>
            {enviando ? <Loader2 className="animate-spin" /> : <MailCheck />}
            Enviar link de redefinição
          </Button>
        </form>
      )}
    </MolduraAuth>
  );
}

/** Tela que o link do e-mail abre: escolhe a nova senha. */
export function RedefinirSenha() {
  const { codigo = '' } = useParams();
  const navegar = useNavigate();

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [concluido, setConcluido] = useState(false);

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);

    if (senha.length < 10) {
      setErro('A senha precisa de 10 caracteres ou mais.');
      return;
    }
    if (senha !== confirmacao) {
      setErro('As senhas não coincidem.');
      return;
    }

    setEnviando(true);
    try {
      await api.redefinirSenha({ codigo, senha });
      setConcluido(true);
      setTimeout(() => navegar('/entrar', { replace: true }), 1800);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível redefinir a senha.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <MolduraAuth
      titulo="Criar nova senha"
      subtitulo="Escolha uma senha com 10 caracteres ou mais, incluindo letras e números."
      rodape={
        <>
          Link vencido?{' '}
          <Link className="font-medium text-primary hover:underline" to="/esqueci-senha">
            Pedir outro
          </Link>
        </>
      }
    >
      {concluido ? (
        <div className="space-y-4 text-sm leading-relaxed">
          <div className="flex items-start gap-3 rounded-md bg-muted/60 p-3">
            <KeyRound className="mt-0.5 size-5 shrink-0 text-primary" />
            <p>Senha alterada. Você já pode entrar com a nova senha.</p>
          </div>
          <Button asChild className="w-full">
            <Link to="/entrar">Entrar agora</Link>
          </Button>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={submeter}>
          <Campo rotulo="Nova senha">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
            />
          </Campo>
          <Campo rotulo="Repita a nova senha">
            <Input
              type="password"
              required
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
              placeholder="••••••••"
            />
          </Campo>

          {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

          <Button type="submit" className="w-full" disabled={enviando}>
            {enviando ? <Loader2 className="animate-spin" /> : <KeyRound />}
            Salvar nova senha
          </Button>
        </form>
      )}
    </MolduraAuth>
  );
}
